import { useCallback, useMemo, useRef, useState } from 'react';
import type { Task } from '../types';
import { addDays, diffDays } from '../dateUtils';
import { MIN_TASK_DURATION_DAYS, RESIZE_HANDLE_PX } from '../constants';

type DragMode = 'move' | 'resize-left' | 'resize-right';

export type UseTaskDragArgs = {
  task: Task;
  rowId: string;

  timelineStart: string;
  columnWidth: number;

  moveTaskDates: (taskId: string, startDate: string, endDate: string) => void;
  moveTaskToRow: (taskId: string, targetRowId: string) => void;
  createRow: () => { id: string };
};

export type UseTaskDragReturn = {
  bind: {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerLeave: () => void;
  };
  cursor: string | undefined;
  isDragging: boolean;
};

const pxToDays = (px: number, columnWidth: number): number => Math.round(px / columnWidth);

const getRowIdFromPoint = (clientX: number, clientY: number): string | null => {
  const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
  if (!el) return null;
  const rowEl = el.closest('[data-row-id]') as HTMLElement | null;
  return rowEl?.dataset?.rowId ?? null;
};

const isBelowGrid = (clientY: number): boolean => {
  const gridEl = document.querySelector('[data-gantt-grid="1"]') as HTMLElement | null;
  if (!gridEl) return false;
  const rect = gridEl.getBoundingClientRect();
  return clientY > rect.bottom;
};

export const useTaskDrag = (args: UseTaskDragArgs): UseTaskDragReturn => {
  const { task, rowId, columnWidth, moveTaskDates, moveTaskToRow, createRow } = args;

  const [cursor, setCursor] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef<{
    pointerId: number;
    mode: DragMode;
    startClientX: number;
    startClientY: number;
    originRowId: string;
    originStartDate: string;
    originEndDate: string;
    lastDayDelta: number;
    hoverRowId: string | null;
  } | null>(null);

  const getModeByOffset = useCallback((offsetX: number, width: number): DragMode => {
    if (offsetX <= RESIZE_HANDLE_PX) return 'resize-left';
    if (offsetX >= width - RESIZE_HANDLE_PX) return 'resize-right';
    return 'move';
  }, []);

  const updateCursorByOffset = useCallback(
    (offsetX: number, width: number) => {
      const mode = getModeByOffset(offsetX, width);
      setCursor(mode === 'move' ? 'grab' : 'col-resize');
    },
    [getModeByOffset]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;

      const mode = getModeByOffset(offsetX, rect.width);

      el.setPointerCapture(e.pointerId);

      dragRef.current = {
        pointerId: e.pointerId,
        mode,
        startClientX: e.clientX,
        startClientY: e.clientY,
        originRowId: rowId,
        originStartDate: task.startDate,
        originEndDate: task.endDate,
        lastDayDelta: 0,
        hoverRowId: rowId,
      };

      setIsDragging(true);
      setCursor(mode === 'move' ? 'grabbing' : 'col-resize');

      e.preventDefault();
      e.stopPropagation();
    },
    [getModeByOffset, rowId, task.endDate, task.startDate]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;

      if (!dragRef.current) {
        updateCursorByOffset(offsetX, rect.width);
        return;
      }

      const st = dragRef.current;
      const dayDelta = pxToDays(e.clientX - st.startClientX, columnWidth);

      // обновление дат — только если изменился dayDelta (чтобы не спамить setState)
      if (dayDelta !== st.lastDayDelta) {
        if (st.mode === 'move') {
          const newStart = addDays(st.originStartDate, dayDelta);
          const newEnd = addDays(st.originEndDate, dayDelta);
          moveTaskDates(task.id, newStart, newEnd);
        }

        if (st.mode === 'resize-left') {
          let newStart = addDays(st.originStartDate, dayDelta);
          const minEnd = addDays(newStart, MIN_TASK_DURATION_DAYS);
          if (diffDays(newStart, st.originEndDate) < MIN_TASK_DURATION_DAYS) {
            // не даём “схлопнуться” меньше минимума, двигая start вперёд
            newStart = addDays(st.originEndDate, -MIN_TASK_DURATION_DAYS);
            void minEnd;
          }
          moveTaskDates(task.id, newStart, st.originEndDate);
        }

        if (st.mode === 'resize-right') {
          let newEnd = addDays(st.originEndDate, dayDelta);
          if (diffDays(st.originStartDate, newEnd) < MIN_TASK_DURATION_DAYS) {
            newEnd = addDays(st.originStartDate, MIN_TASK_DURATION_DAYS);
          }
          moveTaskDates(task.id, st.originStartDate, newEnd);
        }

        st.lastDayDelta = dayDelta;
      }

      // трекинг строки под курсором (фиксируем, но переносим по факту на pointerup)
      const overRowId = getRowIdFromPoint(e.clientX, e.clientY);
      st.hoverRowId = overRowId;

      e.preventDefault();
    },
    [columnWidth, moveTaskDates, task.id, updateCursorByOffset]
  );

  const finishDrag = useCallback((clientX: number, clientY: number) => {
    const st = dragRef.current;
    if (!st) return;

    // перенос между строками / создание новой строки
    const overRowId = getRowIdFromPoint(clientX, clientY);
    if (overRowId && overRowId !== st.originRowId) {
      moveTaskToRow(task.id, overRowId);
    } else if (!overRowId && isBelowGrid(clientY)) {
      const row = createRow();
      moveTaskToRow(task.id, row.id);
    }

    dragRef.current = null;
    setIsDragging(false);
    setCursor(undefined);
  }, [createRow, moveTaskToRow, task.id]);

  const bind = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerLeave: () => {
        if (!dragRef.current) setCursor(undefined);
      },
      // важно: pointerup лучше ловить на window, но для простоты — через onPointerDown захват pointer capture
      onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => finishDrag(e.clientX, e.clientY),
      onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => finishDrag(e.clientX, e.clientY),
    }),
    [finishDrag, onPointerDown, onPointerMove]
  );

  // TS/React типизация: добавляем onPointerUp/onPointerCancel в bind “вручную” (чтобы не плодить отдельный тип)
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bind: bind as any,
    cursor,
    isDragging,
  };
};
